import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "../cx";
import type { Space } from "../tokens/tokens";

// Layout primitives. They only use flex/grid flow + gap, which follow the writing
// direction, so they mirror in RTL with zero per-component code. Class strings are
// literal so Tailwind can see them.

const GAP: Readonly<Record<Space, string>> = {
  0: "gap-0",
  1: "gap-1",
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  8: "gap-8",
  10: "gap-10",
  12: "gap-12",
  16: "gap-16",
};

const ALIGN = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
} as const;

const JUSTIFY = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
} as const;

type Element =
  "div" | "section" | "header" | "footer" | "main" | "aside" | "nav" | "ul" | "ol" | "li" | "form";

interface FlowProps extends HTMLAttributes<HTMLElement> {
  readonly as?: Element;
  readonly gap?: Space;
  readonly align?: keyof typeof ALIGN;
  readonly justify?: keyof typeof JUSTIFY;
  readonly children?: ReactNode;
}

/** Vertical flow (block axis). */
export function Stack({ as: Tag = "div", gap = 4, align, justify, className, ...rest }: FlowProps) {
  return (
    <Tag
      className={cx(
        "flex flex-col",
        GAP[gap],
        align && ALIGN[align],
        justify && JUSTIFY[justify],
        className,
      )}
      {...rest}
    />
  );
}

export interface RowProps extends FlowProps {
  /** Wrap onto new lines when out of inline space. */
  readonly wrap?: boolean;
}

/** Horizontal flow (inline axis): starts at the right in RTL, at the left in LTR. */
export function Row({
  as: Tag = "div",
  gap = 2,
  align = "center",
  justify,
  wrap = false,
  className,
  ...rest
}: RowProps) {
  return (
    <Tag
      className={cx(
        "flex flex-row",
        wrap && "flex-wrap",
        GAP[gap],
        ALIGN[align],
        justify && JUSTIFY[justify],
        className,
      )}
      {...rest}
    />
  );
}

export type GridColumns = 1 | 2 | 3 | 4 | 6 | 12;

const COLS: Readonly<Record<"base" | "sm" | "md" | "lg", Readonly<Record<GridColumns, string>>>> = {
  base: {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    6: "grid-cols-6",
    12: "grid-cols-12",
  },
  sm: {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
    4: "sm:grid-cols-4",
    6: "sm:grid-cols-6",
    12: "sm:grid-cols-12",
  },
  md: {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    6: "md:grid-cols-6",
    12: "md:grid-cols-12",
  },
  lg: {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    6: "lg:grid-cols-6",
    12: "lg:grid-cols-12",
  },
};

export interface GridProps extends Omit<FlowProps, "justify"> {
  /** Column count, optionally per breakpoint: `{ base: 1, md: 3 }`. */
  readonly columns?: GridColumns | Partial<Record<keyof typeof COLS, GridColumns>>;
}

/** Equal-width grid; column 1 sits at the inline start (right in RTL). */
export function Grid({
  as: Tag = "div",
  columns = 1,
  gap = 4,
  align,
  className,
  ...rest
}: GridProps) {
  const spec = typeof columns === "number" ? { base: columns } : columns;
  const colClasses = (Object.keys(spec) as Array<keyof typeof COLS>).map((bp) => {
    const n = spec[bp];
    return n === undefined ? undefined : COLS[bp][n];
  });
  return (
    <Tag
      className={cx("grid", ...colClasses, GAP[gap], align && ALIGN[align], className)}
      {...rest}
    />
  );
}

export interface SidebarLayoutProps extends HTMLAttributes<HTMLDivElement> {
  /** Rendered at the inline start: right in Arabic, left in English. */
  readonly sidebar: ReactNode;
  readonly gap?: Space;
  /** From `md`: shrink the sidebar column to the icon rail (`--size-rail`, 64px). */
  readonly collapsed?: boolean;
  readonly children: ReactNode;
}

/**
 * Two-column layout (sidebar + fluid content). Stacks on small screens; from `md` the
 * sidebar takes `--size-sidebar`. The grid follows the writing direction, so Arabic
 * mirrors it without any direction-specific code.
 */
export function SidebarLayout({
  sidebar,
  gap = 6,
  collapsed = false,
  className,
  children,
  ...rest
}: SidebarLayoutProps) {
  return (
    <div
      className={cx(
        "grid grid-cols-1 md:transition-[grid-template-columns,column-gap] md:duration-200 motion-reduce:transition-none",
        collapsed
          ? "md:grid-cols-[var(--size-rail)_minmax(0,1fr)] md:gap-x-3"
          : "md:grid-cols-[var(--size-sidebar)_minmax(0,1fr)]",
        GAP[gap],
        className,
      )}
      {...rest}
    >
      <aside className="min-w-0" data-collapsed={collapsed ? "true" : undefined}>
        {sidebar}
      </aside>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
